import * as React from 'react';
import { Button, Form, Input, InputNumber, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import DeliverableAssigneeSelect from '../deliverable_assignee_select/DeliverableAssigneeSelect';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Form.List field blocks for the project template's items — the deliverables and
// tasks a project inherits when it is created from the template. Presentational
// only: they read and write the enclosing antd v4 Form at the `name` path they are
// given, so they work both at the top level ('tasks') and nested inside a
// deliverable row ([deliverableName, 'tasks']).
//
// Every list on this form shares one idiom — leading position number, trailing
// remove icon, dashed add button — so deliverables, tasks and the required /
// optional field definitions all read the same way.
//
// Rows are deliberately NOT required-validated: a blank row someone added and left
// alone is discarded on save by the clean* helpers in ProjectTemplateForm (which is
// what the pre-antd-v4 form did), rather than blocking the save until it is removed
// by hand.
//
// A template item carries no due DATE or status: a template has no calendar of its
// own. It does carry a time period in days (daysForDue), which the create form
// turns into a real due date off the project's start (or end) date — see
// getTemplateItemDueDate.

const { TextArea } = Input;

// One registered collection for every deliverable row's assignee picker. Target
// collection names must exist in the buyer_contacts slice's
// standardInitialStateGenerator list, so a per-row name is not an option — and it is
// not needed: OWAsyncSelect resolves a SELECTED value's label from the slice's records
// (getEntityById), not from the target collection, so rows sharing one collection
// share only the dropdown's current page of search results.
const TEMPLATE_DELIVERABLE_ASSIGNEES_TC = 'PROJECT_TEMPLATE_CONTACTS_AUTOCOMPLETE';

const DURATION_TOOLTIP =
	'How many days this takes, counting the first day — 7 days on a project starting Oct 1 is due Oct 7. A deliverable that depends on another counts from that one’s due date instead: 14 days after a Oct 7 deliverable is due Oct 21. On a project with only an end date, counted backwards from it.';

export const TemplateItemRow = ({ index, onRemove, children }) => (
	<div className="flex items-start gap-4">
		<div className="mt-1 w-4 text-character-secondary">{index + 1}</div>
		<div className="flex-1" style={{ maxWidth: 640 }}>
			{children}
		</div>
		<MinusCircleOutlined
			className="mt-2 cursor-pointer text-character-secondary"
			onClick={onRemove}
		/>
	</div>
);

export const TemplateItemAddButton = ({ label, onClick }) => (
	<Form.Item className="mb-0">
		<Button type="dashed" onClick={onClick}>
			<PlusOutlined /> {label}
		</Button>
	</Form.Item>
);

// The item's time period. Same control on deliverables and tasks so the number means
// the same thing in both places.
const TemplateDurationField = ({ restField, name, label = 'Takes (days)' }: any) => (
	<Form.Item
		{...restField}
		label={label}
		name={name}
		tooltip={DURATION_TOOLTIP}
		className="mb-2"
		style={{ width: 160 }}
	>
		<InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="e.g. 14" />
	</Form.Item>
);

interface TaskListFieldsProps {
	// Path in the enclosing form: 'tasks' or [deliverableIndex, 'tasks'].
	name: string | (string | number)[];
	addLabel: string;
}

export const TemplateTaskListFields: React.FC<TaskListFieldsProps> = ({ name, addLabel }) => (
	<Form.List name={name}>
		{(fields, { add, remove }) => (
			<>
				{fields.map(({ key, name: taskName, ...restField }, index) => (
					<TemplateItemRow key={key} index={index} onRemove={() => remove(taskName)}>
						<div className="flex items-start gap-4">
							<Form.Item
								{...restField}
								name={[taskName, 'title']}
								className="mb-2 flex-1"
								style={{ minWidth: 0 }}
							>
								<Input placeholder="Task title" />
							</Form.Item>
							{/* Singular, unlike a deliverable's: a task has one assignee. */}
							<Form.Item
								{...restField}
								name={[taskName, 'assignTo']}
								className="mb-2"
								style={{ width: 220 }}
							>
								<DeliverableAssigneeSelect
									multiple={false}
									targetCollectionName={TEMPLATE_DELIVERABLE_ASSIGNEES_TC}
									placeholder="Unassigned"
								/>
							</Form.Item>
							<TemplateDurationField
								restField={restField}
								name={[taskName, 'daysForDue']}
								label={null}
							/>
						</div>
					</TemplateItemRow>
				))}
				<TemplateItemAddButton label={addLabel} onClick={() => add()} />
			</>
		)}
	</Form.List>
);

interface DeliverableListFieldsProps {
	name: string;
	// Currency code shown on the budget input, e.g. 'USD'.
	currencyId: string;
	// The enclosing form. Needed to offer a row's siblings as its "Depends on" options.
	form: any;
}

export const TemplateDeliverableListFields: React.FC<DeliverableListFieldsProps> = ({
	name,
	currencyId,
	form,
}) => {
	const rows = Form.useWatch(name, form) || [];

	// A deliverable can wait on any of its siblings, by that row's stable id. Itself is
	// excluded — the one dependency the API is certain to reject. Rows with no name yet
	// are still listed, by position, so a half-written template can be wired up.
	const dependencyOptions = (currentId) =>
		rows
			.map((row, index) => ({
				value: nullSafeGetOrElse('id', row, ''),
				label: `${index + 1}. ${nullSafeGetOrElse('name', row, 'Untitled deliverable')}`,
			}))
			.filter((option) => option.value && option.value !== currentId);

	return (
		<Form.List name={name}>
			{(fields, { add, remove }) => (
				<>
					{fields.map(({ key, name: deliverableName, ...restField }, index) => (
						<TemplateItemRow key={key} index={index} onRemove={() => remove(deliverableName)}>
							{/* Left rule groups the deliverable's own fields and its task list,
						    so it stays readable next to the single-field task rows. */}
							<div className="mb-4 border-0 border-l border-solid border-owgray pl-4">
								<Form.Item {...restField} label="Name" name={[deliverableName, 'name']}>
									<Input />
								</Form.Item>
								<Form.Item
									{...restField}
									label="Description"
									name={[deliverableName, 'description']}
								>
									<TextArea autoSize={{ minRows: 2 }} />
								</Form.Item>
								<div className="flex items-start gap-4">
									<Form.Item
										{...restField}
										label="Budget"
										name={[deliverableName, 'budget']}
										className="mb-2 flex-1"
										style={{ minWidth: 0 }}
									>
										<InputNumber
											min={0}
											style={{ display: 'flex', width: '100%' }}
											addonAfter={currencyId}
										/>
									</Form.Item>
									<TemplateDurationField
										restField={restField}
										name={[deliverableName, 'daysForDue']}
									/>
								</div>
								<Form.Item
									{...restField}
									label="Default assignees"
									name={[deliverableName, 'assignTo']}
									tooltip="Everyone here is assigned to this deliverable when a project is created from the template. Assignees can still be changed on the project."
								>
									<DeliverableAssigneeSelect
										targetCollectionName={TEMPLATE_DELIVERABLE_ASSIGNEES_TC}
										placeholder="Nobody by default"
									/>
								</Form.Item>
								{/* Referenced by the sibling's id, so adding, removing or reordering rows
							    cannot re-point it at a different deliverable. */}
								<Form.Item
									{...restField}
									label="Depends on"
									name={[deliverableName, 'dependsOnId']}
									tooltip="The deliverable that has to finish first. This one's days then count from that deliverable's due date rather than from the project start, and the dependency carries onto the project so a later date change cascades."
								>
									<Select
										allowClear
										showSearch
										optionFilterProp="label"
										placeholder="No dependency"
										options={dependencyOptions(nullSafeGetOrElse('id', rows[deliverableName], ''))}
									/>
								</Form.Item>
								<div className="bodymedium mb-2">Tasks</div>
								<TemplateTaskListFields name={[deliverableName, 'tasks']} addLabel="Add task" />
							</div>
						</TemplateItemRow>
					))}
					<TemplateItemAddButton
						label="Add deliverable"
						// The id is minted here, once, and travels with the row for its lifetime.
						onClick={() => add({ id: uuidv4(), tasks: [], assignTo: [] })}
					/>
				</>
			)}
		</Form.List>
	);
};
